import { registerSchema } from '@argus-labs/auth-shared/auth'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PageCard } from '@/components/ui/page-card'
import { authClient } from '@/lib/auth-client'
import { getSessionQueryOptions, invalidateSessionQuery } from '@/lib/session'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/_auth/edit/name')({
  component: EditName,
})

function EditName() {
  return (
    <PageCard title="Edit Full Name" description="Change your full name.">
      <EditNameForm />
    </PageCard>
  )
}

// Pick name from register params
const editNameFormSchema = registerSchema.pick({ name: true })
type EditNameFormParams = z.infer<typeof editNameFormSchema>

export function EditNameForm() {
  const {
    data: { data },
  } = useSuspenseQuery(getSessionQueryOptions())
  const name = data?.user.name
  const form = useForm<EditNameFormParams>({
    resolver: zodResolver(editNameFormSchema),
    defaultValues: { name },
  })
  const navigate = useNavigate()

  async function onSubmit(values: EditNameFormParams) {
    try {
      const { data, error } = await authClient.updateUser({
        name: values.name,
      })

      if (data) {
        invalidateSessionQuery()

        return navigate({ to: '/account' })
      }

      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2">
      <Input
        control={form.control}
        type="text"
        name="name"
        label="Full Name"
        required
        autoFocus
        autoComplete="name"
        placeholder="eg: mojo doe"
      />

      <Button type="submit" flex isDisabled={form.formState.isSubmitting}>
        Update
      </Button>
    </form>
  )
}
